/**
 * A small pill for a count, a tag, or a piece of metadata.
 *
 * Distinct from `StatusBadge`, which carries a state and pairs its colour with
 * an icon. This one is for things colour does not have to explain: a credit
 * value, a module code, a role name.
 *
 * Three treatments: `solid` fills with the tone, `soft` tints the ground and
 * writes in the tone's readable step, `outline` is a hairline for a tag that
 * should not compete. Soft is the one that carries state without shouting, so
 * it is what the status badges use.
 *
 * @module components/ui/Badge
 */

import React, { type ReactNode } from "react";

import { Badge as DaisyBadge } from "react-daisyui";

export const BADGE_TONES = [
  "neutral",
  "ink",
  "primary",
  "success",
  "warning",
  "error",
  "ghost",
] as const;
export type BadgeTone = (typeof BADGE_TONES)[number];

export const BADGE_VARIANTS = ["solid", "soft", "outline"] as const;
export type BadgeVariant = (typeof BADGE_VARIANTS)[number];

export interface BadgeProps {
  children: ReactNode;
  tone?: BadgeTone;
  size?: "xs" | "sm" | "md";
  /**
   * How the tone is painted. `soft` is the default everywhere; `solid` is for a
   * tiny indicator that has to stay saturated.
   */
  variant?: BadgeVariant;
  /**
   * Names the badge where its text does not. A count renders as a bare
   * numeral, which a screen reader announces as "1" between a heading and a
   * button, saying nothing about what was counted.
   */
  "aria-label"?: string;
  className?: string;
  "data-testid"?: string;
}

/**
 * The daisyUI class each tone emits, which is not always its own name: `ink`
 * is daisyUI's `neutral`, and this `neutral` is the unset badge. Exported so
 * the safelist test asserts the class that actually ships rather than a name.
 */
export const BADGE_TONE_COLOR: Record<
  BadgeTone,
  "neutral" | "primary" | "success" | "warning" | "error" | "ghost" | undefined
> = {
  // daisyUI has no unsaturated chip colour: its unset badge fills with base-100
  // and its `neutral` is the Ink fill. So this tone carries no colour and takes
  // its fill from the soft variant below, and `ink` is the Ink one, which names
  // the thing the chrome belongs to.
  neutral: undefined,
  ink: "neutral",
  primary: "primary",
  success: "success",
  warning: "warning",
  error: "error",
  ghost: "ghost",
};

const VARIANT_CLASS: Record<BadgeVariant, string> = {
  solid: "",
  soft: "badge-soft",
  outline: "badge-outline",
};

/**
 * @example
 * ```tsx
 * <Badge tone="ghost" size="sm">10 ECTS</Badge>
 * ```
 */
export function Badge({
  children,
  tone = "neutral",
  size = "sm",
  variant = "soft",
  "aria-label": label = undefined,
  className = "",
  "data-testid": testId,
}: BadgeProps): React.ReactElement {
  // Measured in a browser: daisyUI's unset badge fills with base-100, so on a
  // base-100 surface it is 1.00:1 against what is behind it and on base-200 it
  // is 1.09:1 — an absent chip rather than a pale one. Uncoloured `badge-soft`
  // reads 1.14 and 1.25 on the same two, and an explicit variant still wins.
  const painted =
    tone === "neutral" && variant === "solid" ? VARIANT_CLASS.soft : VARIANT_CLASS[variant];

  return (
    <DaisyBadge
      aria-label={label}
      // daisyUI's badge is a bare `div`, and `aria-label` on one with no role
      // is prohibited: a reader may announce the numeral, not the sentence.
      // `img` names a graphic whose label replaces its contents, which is what
      // a count chip is. Unnamed badges stay text, and read with their line.
      role={label === undefined ? undefined : "img"}
      color={BADGE_TONE_COLOR[tone]}
      size={size === "md" ? undefined : size}
      // px-2 because daisyUI 5 gives a badge almost no horizontal padding at
      // the small sizes, which reads as a cramped box rather than a pill.
      className={["px-2 whitespace-nowrap", painted, className].filter(Boolean).join(" ")}
      data-testid={testId}
    >
      {children}
    </DaisyBadge>
  );
}
